import { useEffect, useState } from "react";

import { useStore } from "@/state/store";
import { ProbabilityRing } from "@/components/atoms/ProbabilityRing";
import { Pill } from "@/components/atoms/Pill";
import { fmtMarketMetric, fmtShortDate } from "@/components/atoms/format";
import { marketMetrics, type MarketWithMetrics } from "@/api/marketMetrics";
import { useMid } from "./useMid";

type MarketView = MarketWithMetrics;

interface Props {
  markets: MarketView[];
}

/** Metrica del featured market: label sopra, valore grande in mono.
 *  Più prominente della riga compatta delle card in griglia. */
function HeroMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted">
        {label}
      </div>
      <div className="mt-[2px] font-mono text-[18px] font-semibold text-text-2">
        {value}
      </div>
    </div>
  );
}

export function HeroStrip({ markets }: Props) {
  const openDetail = useStore((s) => s.openDetail);
  const openTrade = useStore((s) => s.openTrade);
  const [idx, setIdx] = useState(0);

  // Auto-rotate ogni 9s.
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % Math.max(markets.length, 1));
    }, 9_000);
    return () => clearInterval(t);
  }, [markets.length]);

  if (markets.length === 0) return null;
  const m = markets[idx % markets.length];
  const mid = useMid(m.slug, 5000);
  const yes = mid;
  const yesOutcome = m.outcomes[0];
  const noOutcome = m.outcomes[1];
  const { volume, liquidity } = marketMetrics(m);
  // I market categorical non hanno un "YES": mostrare Buy YES/NO e un anello
  // di probabilità binaria su 5 gambe sarebbe fuorviante.
  const isMulti = m.outcomes.length > 2;
  const uniformOdds = 1 / Math.max(m.outcomes.length, 1);

  return (
    <div className="card relative overflow-hidden p-7">
      <div className="grid items-center gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted">
            <span className="rounded bg-accent-soft px-2 py-[2px] text-accent-2">
              Featured
            </span>
            <span>{m.category}</span>
            <Pill variant="accent">{m.status.toUpperCase()}</Pill>
          </div>
          <button
            type="button"
            onClick={() => openDetail(m.slug)}
            className="mt-3 block text-left transition-colors hover:text-accent-2"
          >
            <h1 className="text-[28px] font-semibold leading-tight tracking-tight">
              {m.title}
            </h1>
          </button>
          <p className="mt-2 max-w-2xl text-[13.5px] text-text-2">
            {m.description}
          </p>
          {isMulti ? (
            // Categorical: una gamba per outcome. Senza orderbook per gamba la
            // probabilità è uniforme (1/N) — stesso criterio usato dalle card.
            <div className="mt-4 flex flex-wrap gap-2">
              {m.outcomes.slice(0, 4).map((o) => (
                <button
                  key={o.id}
                  className="btn-yn yes !px-4 !py-2 !text-[13px]"
                  onClick={() => openTrade(m, o.id, "buy")}
                >
                  <span className="max-w-[160px] truncate">{o.label}</span>
                  <span className="ml-3 font-mono">
                    {(uniformOdds * 100).toFixed(0)}¢
                  </span>
                </button>
              ))}
              {m.outcomes.length > 4 && (
                <button
                  type="button"
                  onClick={() => openDetail(m.slug)}
                  className="px-3 py-2 text-[12.5px] text-muted hover:text-text-2"
                >
                  +{m.outcomes.length - 4} altri
                </button>
              )}
            </div>
          ) : (
            <div className="mt-4 flex gap-3">
              {yesOutcome && (
                <button
                  className="btn-yn yes !px-5 !py-3 !text-[14px]"
                  onClick={() => openTrade(m, yesOutcome.id, "buy")}
                >
                  <span>Buy YES</span>
                  <span className="ml-3 font-mono">{(yes * 100).toFixed(1)}¢</span>
                </button>
              )}
              {noOutcome && (
                <button
                  className="btn-yn no !px-5 !py-3 !text-[14px]"
                  onClick={() => openTrade(m, noOutcome.id, "buy")}
                >
                  <span>Buy NO</span>
                  <span className="ml-3 font-mono">
                    {((1 - yes) * 100).toFixed(1)}¢
                  </span>
                </button>
              )}
            </div>
          )}
          <div className="mt-5 flex gap-8">
            <HeroMetric label="Volume" value={fmtMarketMetric(volume)} />
            <HeroMetric label="Liquidity" value={fmtMarketMetric(liquidity)} />
          </div>
          <div className="mt-4 text-[11px] text-muted">
            Resolves {fmtShortDate(m.resolve_after)} · {m.outcomes.length}{" "}
            outcomes
          </div>
        </div>
        <div className="flex items-center justify-center">
          {isMulti ? (
            <div className="flex flex-col items-center gap-1">
              <div className="font-mono text-[44px] font-semibold leading-none">
                {m.outcomes.length}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted">
                outcomes
              </div>
            </div>
          ) : (
            <ProbabilityRing value={yes * 100} size={196} />
          )}
        </div>
      </div>

      {markets.length > 1 && (
        <div className="absolute bottom-3 right-4 flex gap-1">
          {markets.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show market ${i + 1}`}
              className={`h-[6px] rounded-full transition-all ${
                i === idx % markets.length
                  ? "w-6 bg-accent"
                  : "w-2 bg-line-strong hover:bg-muted"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
